import { useHistory } from 'react-router-dom';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { fulfillmentGroupApi } from '../redux/apis/endpoints/fulfillmentGroupApi';
import { orderItemApi } from '../redux/apis/endpoints/orderItemApi';
import { orderApi } from '../redux/apis/endpoints/orderApi';

const useActionHandler = () => {
  const history = useHistory();
  const [activeModals, setActiveModals] = useState({});

  // RTK Query hooks
  const [cancelFulfillmentGroup] = fulfillmentGroupApi.useCancelFulfillmentGroupMutation();
  const [confirmFulfillmentGroup] = fulfillmentGroupApi.useConfirmFulfillmentGroupMutation();
  const [requestGroupReturn] = fulfillmentGroupApi.useRequestGroupReturnMutation();
  const [cancelGroupReturn] = fulfillmentGroupApi.useCancelGroupReturnMutation();
  const [approveGroupReturn] = fulfillmentGroupApi.useApproveGroupReturnMutation();
  const [rejectGroupReturn] = fulfillmentGroupApi.useRejectGroupReturnMutation();
  const [confirmReturnDelivery] = fulfillmentGroupApi.useConfirmReturnDeliveryMutation();
  const [updateProductConditions] = fulfillmentGroupApi.useUpdateProductConditionsMutation();

  // RTK Query hooks - Order Item
  const [cancelOrderItem] = orderItemApi.useCancelOrderItemMutation();
  const [initiateItemReturn] = orderItemApi.useInitiateItemReturnMutation();
  const [cancelReturnRequest] = orderItemApi.useCancelReturnRequestMutation();
  const [startReturnShipping] = orderItemApi.useStartReturnShippingMutation();
  const [confirmOrderItem] = orderItemApi.useConfirmOrderItemMutation();
  const [keepRentalItem] = orderItemApi.useKeepRentalItemMutation();
  const [extendRental] = orderItemApi.useExtendRentalMutation();

  const handleAction = async (action, entity, userRole, mutationHooks = {}) => {
    const { action: actionConfig } = action;

    try {
      switch (actionConfig.type) {
        case 'api':
          return await handleApiAction(actionConfig, entity, mutationHooks);
        
        case 'modal':
          return await handleModalAction(actionConfig, entity, action.id, mutationHooks);
        
        case 'redirect':
          return handleRedirectAction(actionConfig, entity);
        
        case 'download':
          return await handleDownloadAction(actionConfig, entity);
        
        default:
          throw new Error(`Unknown action type: ${actionConfig.type}`);
      }
    } catch (error) {
      console.error('Action execution failed:', error);
      toast.error(error.message || 'Action failed. Please try again.');
      return { success: false, error: error.message };
    }
  };

  const handleApiAction = async (actionConfig, entity, mutationHooks = {}) => {
    const { mutation, api } = actionConfig;
    
    // Handle new RTK Query format (api as string + mutation)
    if (mutation && api && typeof api === 'string') {
      const mutationHook = getMutationHook(mutation, api);
      
      if (mutationHook) {
      try {
          const params = prepareMutationParams(mutation, entity);
          const result = await mutationHook(params).unwrap();
        return { success: true, data: result };
      } catch (error) {
        throw new Error(error.data?.message || error.message || 'Mutation failed');
      }
      } else {
        throw new Error(`No mutation hook found for ${mutation} in ${api}`);
      }
    }
    
    // Handle legacy format (api as object with endpoint/method)
    if (api && typeof api === 'object' && api.endpoint && api.method) {
      // Legacy handling code would go here
      throw new Error('Legacy API format not implemented yet');
    }
    
    throw new Error('No valid API configuration found');
  };

  const handleModalAction = async (actionConfig, entity, actionId, mutationHooks = {}) => {
    const { component, mutation, api } = actionConfig;
    
    return new Promise((resolve) => {
      // Set modal state to show the modal
      setActiveModals(prev => ({
        ...prev,
        [actionId]: {
          component,
          entity,
          onConfirm: async (modalData) => {
            try {
              // Close modal first
              setActiveModals(prev => {
                const newModals = { ...prev };
                delete newModals[actionId];
                return newModals;
              });

              // Execute API call if configured, otherwise just return modal data
              if (mutation && api) {
                const result = await handleApiAction({ mutation, api }, { ...entity, ...modalData }, mutationHooks);
                resolve(result);
              } else {
                // Modal handles API calls internally, just return the result
                resolve(modalData);
              }
            } catch (error) {
              resolve({ success: false, error: error.message });
            }
          },
          onCancel: () => {
            setActiveModals(prev => {
              const newModals = { ...prev };
              delete newModals[actionId];
              return newModals;
            });
            resolve({ success: false, cancelled: true });
          }
        }
      }));
    });
  };

  const handleRedirectAction = (actionConfig, entity) => {
    const url = interpolateUrl(actionConfig.target, entity);
    history.push(url);
    return { success: true, redirected: true };
  };

  const handleDownloadAction = async (actionConfig, entity) => {
    const { api } = actionConfig;
    const url = interpolateUrl(api.endpoint, entity);

    const response = await fetch(url, {
      method: api.method || 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      throw new Error(`Download failed: ${response.statusText}`);
    }

    // Handle file download
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = getFilenameFromResponse(response) || `export-${entity.id}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);

    return { success: true, downloaded: true };
  };

  // Helper function to get the appropriate mutation hook
  const getMutationHook = (mutationName, api) => {
    switch (api) {
      case 'fulfillmentGroupApi':
        switch (mutationName) {
          case 'useCancelFulfillmentGroupMutation':
            return cancelFulfillmentGroup;
          case 'useConfirmFulfillmentGroupMutation':
            return confirmFulfillmentGroup;
          case 'useRequestGroupReturnMutation':
            return requestGroupReturn;
          case 'useCancelGroupReturnMutation':
            return cancelGroupReturn;
          case 'useApproveGroupReturnMutation':
            return approveGroupReturn;
          case 'useRejectGroupReturnMutation':
            return rejectGroupReturn;
          case 'useConfirmReturnDeliveryMutation':
            return confirmReturnDelivery;
          case 'useUpdateProductConditionsMutation':
            return updateProductConditions;
          default:
            return null;
        }
      case 'orderItemApi':
        switch (mutationName) {
          case 'useCancelOrderItemMutation':
            return cancelOrderItem;
          case 'useInitiateItemReturnMutation':
            return initiateItemReturn;
          case 'useCancelReturnRequestMutation':
            return cancelReturnRequest;
          case 'useKeepRentalItemMutation':
            return keepRentalItem;
          case 'useExtendRentalMutation':
            return extendRental;
          default:
            return null;
        }
      default:
        return null;
    }
  };

  // Helper function to interpolate URLs with entity data
  const interpolateUrl = (urlTemplate, entity) => {
    return urlTemplate.replace(/\{(\w+)\}/g, (match, key) => {
      return entity[key] || entity.id || match;
    });
  };

  // Helper function to interpolate payload with entity data
  const interpolatePayload = (payloadTemplate, entity) => {
    const payload = { ...payloadTemplate };
    
    // Add entity ID and common fields to payload
    payload.entityId = entity.id;
    payload.entityType = entity.entityType;
    
    return payload;
  };

  // Helper function to extract filename from response headers
  const getFilenameFromResponse = (response) => {
    const contentDisposition = response.headers.get('Content-Disposition');
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="([^"]+)"/);
      return match ? match[1] : null;
    }
    return null;
  };

  // Helper function to generate mutation key for RTK Query hooks (legacy support)
  const getMutationKey = (endpoint, method) => {
    // Normalize endpoint to match mutation hook naming patterns
    if (endpoint.includes('/confirm') && method === 'PUT') {
      if (endpoint.includes('fulfillment-groups')) {
        return 'confirmFulfillmentGroup';
      }
    }
    if (endpoint.includes('/cancel') && (method === 'PUT' || method === 'DELETE')) {
      if (endpoint.includes('fulfillment-groups')) {
        return 'cancelFulfillmentGroup';
      }
    }
    // Add more mappings as needed
    return null;
  };

  // Helper function to prepare mutation parameters
  const prepareMutationParams = (mutationNameOrApi, entity) => {
    // Handle new format: mutation name
    if (typeof mutationNameOrApi === 'string') {
      const mutationName = mutationNameOrApi;
      
      switch (mutationName) {
        case 'useCancelFulfillmentGroupMutation':
          return {
            orderId: entity.orderId || entity.order?.id,
            fulfillmentGroupId: entity.groupId || entity.id,
            cancelReason: entity.reason || entity.cancelReason,
            cancelComments: entity.comments || entity.cancelComments
          };
        case 'useConfirmFulfillmentGroupMutation':
          return {
            orderId: entity.orderId || entity.order?.id,
            fulfillmentGroupId: entity.groupId || entity.id
          };
        case 'useRequestGroupReturnMutation':
          return {
            fulfillmentGroupId: entity.groupId || entity.id,
            reason: entity.returnReason || entity.reason || 'Customer requested return',
            comments: entity.returnComments || entity.comments || '',
            itemIds: entity.itemIds || []
          };
        case 'useCancelGroupReturnMutation':
          return {
            fulfillmentGroupId: entity.groupId || entity.id
          };
        case 'useApproveGroupReturnMutation':
          return {
            fulfillmentGroupId: entity.groupId || entity.id,
            approvalComments: entity.approvalComments || ''
          };
        case 'useRejectGroupReturnMutation':
          return {
            fulfillmentGroupId: entity.groupId || entity.id,
            rejectionReason: entity.rejectionReason || ''
          };
        case 'useConfirmReturnDeliveryMutation':
          return {
            fulfillmentGroupId: entity.groupId || entity.id,
            deliveryComments: entity.deliveryComments || ''
          };
        case 'useUpdateProductConditionsMutation':
          return {
            fulfillmentGroupId: entity.groupId || entity.id,
            productUpdates: entity.productUpdates || []
          };
        default:
          return {
            ...entity,
            entityId: entity.id,
            entityType: entity.entityType
          };
      }
    }
    
    // Handle legacy format: api object
    const api = mutationNameOrApi;
    const endpoint = api.endpoint;
    
    if (endpoint.includes('fulfillment-groups') && endpoint.includes('confirm')) {
      return {
        orderId: entity.orderId || entity.order?.id,
        fulfillmentGroupId: entity.groupId || entity.id
      };
    }
    
    if (endpoint.includes('fulfillment-groups') && endpoint.includes('cancel')) {
      return {
        orderId: entity.orderId || entity.order?.id,
        fulfillmentGroupId: entity.groupId || entity.id,
        cancelReason: entity.reason || entity.cancelReason,
        cancelComments: entity.comments || entity.cancelComments
      };
    }
    
    // Default parameter preparation
    return {
      ...entity,
      entityId: entity.id,
      entityType: entity.entityType
    };
  };

  return {
    handleAction,
    activeModals,
    setActiveModals
  };
};

export default useActionHandler; 